import React, { useEffect, useState } from "react";
import { Save, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AdminHeader } from "@/components/AdminHeader";

const Row = ({ r, onSaved }) => {
  const [form, setForm] = useState({ artist: r.artist, title: r.title, weeks_at_no1: r.weeks_at_no1 ?? "" });
  const [saving, setSaving] = useState(false);
  const dirty = form.artist !== r.artist || form.title !== r.title || String(form.weeks_at_no1) !== String(r.weeks_at_no1 ?? "");

  const save = async () => {
    setSaving(true);
    try {
      const body = { artist: form.artist, title: form.title };
      if (form.weeks_at_no1 !== "") body.weeks_at_no1 = Number(form.weeks_at_no1);
      const res = await api.put(`/admin/number-ones/${r.chart_week}`, body, { params: { chart: r.chart } });
      onSaved(res.data);
      toast.success(`${r.chart_week} saved`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr data-testid={`no1-row-${r.chart_week}`} className="border-b border-slate-100">
      <td className="py-2.5 pr-3 font-mono text-xs text-ink-muted whitespace-nowrap">{r.chart_week}{r.edited && <span className="ml-1 text-gold" title="Edited">•</span>}</td>
      <td className="py-2.5 pr-3"><input data-testid={`no1-artist-${r.chart_week}`} value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-ink focus:border-gold outline-none" /></td>
      <td className="py-2.5 pr-3"><input data-testid={`no1-title-${r.chart_week}`} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-ink focus:border-gold outline-none" /></td>
      <td className="py-2.5 pr-3 w-20"><input data-testid={`no1-weeks-${r.chart_week}`} type="number" min="1" value={form.weeks_at_no1} onChange={(e) => setForm({ ...form, weeks_at_no1: e.target.value })} className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-ink focus:border-gold outline-none" /></td>
      <td className="py-2.5">
        <button data-testid={`no1-save-${r.chart_week}`} onClick={save} disabled={!dirty || saving} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-ink text-white font-mono text-xs disabled:opacity-30 transition-opacity">
          <Save className="w-3.5 h-3.5" /> {saving ? "…" : "Save"}
        </button>
      </td>
    </tr>
  );
};

export default function AdminNumberOnes() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 60 });
  const [q, setQ] = useState("");
  const [year, setYear] = useState("");
  const [chart, setChart] = useState("uk_singles");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/number-ones", { params: { q: q || undefined, year: year || undefined, chart, page } })
      .then((r) => setData(r.data)).catch(() => toast.error("Could not load number ones")).finally(() => setLoading(false));
  }, [q, year, page, chart]);

  const onSaved = (row) => setData((d) => ({ ...d, items: d.items.map((o) => (o.chart_week === row.chart_week ? row : o)) }));
  const pages = Math.max(1, Math.ceil(data.total / data.limit));

  return (
    <div className="min-h-screen bg-paper">
      <AdminHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-number-ones-page">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Number Ones</p>
        <h1 className="mt-1 text-3xl font-bold text-ink">No.1 charts — weekly chart rows</h1>
        <p className="mt-2 text-sm text-ink-muted">{data.total.toLocaleString()} chart weeks in this chart. Edit artist, title or weeks; the homepage, /number-ones, birthday milestones and emails update immediately.</p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <select data-testid="no1-admin-chart" value={chart} onChange={(e) => { setChart(e.target.value); setPage(1); }} className="h-9 rounded-full border border-slate-200 bg-white px-4 text-sm text-ink focus:border-gold outline-none">
            {(data.charts || [{ chart: "uk_singles", label: "UK Singles" }]).map((c) => <option key={c.chart} value={c.chart}>{c.label}</option>)}
          </select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
            <input data-testid="no1-admin-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search artist, title or date" className="h-9 w-72 rounded-full border border-slate-200 bg-white pl-9 pr-4 text-sm text-ink focus:border-gold outline-none" />
          </div>
          <input data-testid="no1-admin-year" type="number" min="1952" max="2030" value={year} onChange={(e) => { setYear(e.target.value); setPage(1); }} placeholder="Year" className="h-9 w-24 rounded-full border border-slate-200 bg-white px-4 text-sm text-ink focus:border-gold outline-none" />
          <div className="ml-auto flex items-center gap-2 font-mono text-xs text-ink-muted">
            <button data-testid="no1-admin-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 h-8 rounded-full border border-slate-200 bg-white disabled:opacity-30">Prev</button>
            <span>Page {page} / {pages}</span>
            <button data-testid="no1-admin-next" disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-3 h-8 rounded-full border border-slate-200 bg-white disabled:opacity-30">Next</button>
          </div>
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white px-4">
          <table className="w-full text-left">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-widest text-ink-muted border-b border-slate-200">
                <th className="py-3 pr-3">Chart week</th><th className="py-3 pr-3">Artist</th><th className="py-3 pr-3">Title</th><th className="py-3 pr-3">Weeks</th><th className="py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="py-10 text-center text-sm text-ink-muted">Loading…</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={5} className="py-10 text-center text-sm text-ink-muted" data-testid="no1-admin-empty">No rows match.</td></tr>
              ) : data.items.map((r) => <Row key={r.chart_week} r={r} onSaved={onSaved} />)}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
